import type { AnalyticsConfig } from '~/lib/config/env';

const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/i;
const GTAG_SCRIPT_ID = 'google-analytics-gtag';

type GoogleTagFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: GoogleTagFunction;
  }
}

let initializedMeasurementId: string | null = null;
let scriptLoadScheduled = false;

export function isGoogleAnalyticsEnabled(config: AnalyticsConfig): boolean {
  return (
    config.enabled &&
    GA_MEASUREMENT_ID_PATTERN.test(config.measurementId.trim())
  );
}

export function sanitizePageLocation(pageLocation: string): string {
  const fallbackOrigin =
    typeof window === 'undefined'
      ? 'https://trypillian.com'
      : window.location.origin;
  const url = new URL(pageLocation, fallbackOrigin);

  return `${url.origin}${url.pathname}`;
}

export function initializeGoogleAnalytics(config: AnalyticsConfig): void {
  if (typeof window === 'undefined' || !isGoogleAnalyticsEnabled(config)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer?.push(arguments);
    };

  if (initializedMeasurementId === config.measurementId) {
    return;
  }

  setDefaultConsent();

  window.gtag('js', new Date());
  window.gtag('config', config.measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    debug_mode: config.debug,
  });

  initializedMeasurementId = config.measurementId;
  scheduleGtagScriptLoad(config);
}

export function trackGoogleAnalyticsPageView(
  config: AnalyticsConfig,
  pageLocation: string,
  pageTitle: string
): void {
  if (typeof window === 'undefined' || !isGoogleAnalyticsEnabled(config)) {
    return;
  }

  initializeGoogleAnalytics(config);

  window.gtag?.('event', 'page_view', {
    page_location: sanitizePageLocation(pageLocation),
    page_title: pageTitle,
    send_to: config.measurementId,
  });
}

function setDefaultConsent(): void {
  window.gtag?.('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
}

function scheduleGtagScriptLoad(config: AnalyticsConfig): void {
  if (
    scriptLoadScheduled ||
    typeof document === 'undefined' ||
    document.getElementById(GTAG_SCRIPT_ID)
  ) {
    return;
  }

  scriptLoadScheduled = true;

  const loadScript = () => {
    if (document.getElementById(GTAG_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      config.measurementId
    )}`;
    document.head.appendChild(script);
  };

  if (config.loadStrategy === 'idle' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(loadScript, { timeout: 2000 });
    return;
  }

  window.setTimeout(loadScript, 0);
}

export function resetGoogleAnalyticsForTests(): void {
  initializedMeasurementId = null;
  scriptLoadScheduled = false;
}
